import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import API_URL from '../config/api';

// Token storage in memory (survives within session, not across tabs)
let accessToken = null;
let refreshToken = null;

// Persist tokens to sessionStorage as backup
function saveTokens(access, refresh) {
  accessToken = access;
  refreshToken = refresh;
  if (access) sessionStorage.setItem('ll_at', access);
  else sessionStorage.removeItem('ll_at');
  if (refresh) sessionStorage.setItem('ll_rt', refresh);
  else sessionStorage.removeItem('ll_rt');
}

function loadTokens() {
  if (!accessToken) accessToken = sessionStorage.getItem('ll_at');
  if (!refreshToken) refreshToken = sessionStorage.getItem('ll_rt');
}

// Configure axios: always send credentials (cookies) AND Authorization header
axios.defaults.withCredentials = true;

// Request interceptor: attach token to every request
axios.interceptors.request.use((config) => {
  loadTokens();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Response interceptor: auto-refresh on 401
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/login') && !originalRequest.url?.includes('/auth/register')) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          loadTokens();
          const headers = {};
          if (refreshToken) headers['X-Refresh-Token'] = refreshToken;
          const res = await axios.post(`${API_URL}/api/auth/refresh`, {}, { headers, _retry: true });
          const newAccess = res.data.access_token;
          if (newAccess) {
            saveTokens(newAccess, refreshToken);
            onRefreshed(newAccess);
          }
          isRefreshing = false;
          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          return axios(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          saveTokens(null, null);
          return Promise.reject(error);
        }
      }

      // Queue requests while refreshing
      return new Promise((resolve) => {
        refreshSubscribers.push((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(axios(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    loadTokens();
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
      saveTokens(null, null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    if (response.data.access_token) {
      saveTokens(response.data.access_token, response.data.refresh_token);
    }
    setUser(response.data.user);
    return response.data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    if (response.data.access_token) {
      saveTokens(response.data.access_token, response.data.refresh_token);
    }
    setUser(response.data.user);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (err) {
      // Intentional: logout must clear local state even if the server-side session
      // invalidation fails (network error, expired token, etc.). User is logged out regardless.
    }
    setUser(null);
    saveTokens(null, null);
  }, []);

  const googleLogin = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const exchangeSession = useCallback(async (sessionId) => {
    const response = await axios.post(`${API_URL}/api/auth/session`, { session_id: sessionId });
    if (response.data.access_token) {
      saveTokens(response.data.access_token, response.data.refresh_token);
    }
    setUser(response.data.user);
    return response.data;
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      return null;
    }
  }, []);

  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    googleLogin,
    exchangeSession,
    updateUser,
    refreshUser,
    isAuthenticated: !!user
  }), [user, loading, login, register, logout, googleLogin, exchangeSession, updateUser, refreshUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
